import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const MENU_GAP = 8;
const VIEWPORT_MARGIN = 12;

export default function SearchableSettingSelect({
  title,
  value,
  options,
  onChange,
  placeholder = "Search...",
  emptyMessage = "No matches found.",
  minQueryLength = 0,
  maxVisibleOptions = 30,
  disabled = false,
  renderSelectedValue,
  renderOption
}) {
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuStyle, setMenuStyle] = useState(null);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) || null,
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    const normalizedQuery = String(query || "").trim().toLowerCase();

    if (normalizedQuery.length < minQueryLength) {
      return [];
    }

    if (!normalizedQuery) {
      return options.slice(0, maxVisibleOptions);
    }

    return options
      .filter((option) => String(option.label || option.value || "").trim().toLowerCase().includes(normalizedQuery))
      .slice(0, maxVisibleOptions);
  }, [maxVisibleOptions, minQueryLength, options, query]);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  useEffect(() => {
    if (!isOpen) {
      setMenuStyle(null);
      setQuery("");
      return undefined;
    }

    const updateMenuPosition = () => {
      const element = containerRef.current;

      if (!element) {
        return;
      }

      const rect = element.getBoundingClientRect();
      const maxHeight = Math.max(220, window.innerHeight - rect.bottom - MENU_GAP - VIEWPORT_MARGIN);

      setMenuStyle({
        position: "fixed",
        left: rect.left,
        top: rect.bottom + MENU_GAP,
        width: rect.width,
        maxHeight,
        zIndex: 1600
      });
    };

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    const focusTimeoutId = window.setTimeout(() => inputRef.current?.focus(), 0);

    return () => {
      window.clearTimeout(focusTimeoutId);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (containerRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) {
        return;
      }

      setIsOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className={`account-setting-select-shell${isOpen ? " account-setting-select-shell--open" : ""}`}>
      <button
        type="button"
        className="account-setting-select-trigger"
        onClick={() => {
          if (disabled) {
            return;
          }

          setIsOpen((currentValue) => !currentValue);
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={title}
        disabled={disabled}
      >
        <div className="account-setting-select-preview">
          {selectedOption
            ? (renderSelectedValue
              ? renderSelectedValue(selectedOption.value, selectedOption)
              : <span className="account-setting-select-preview-label">{selectedOption.label}</span>)
            : <span className="account-setting-select-preview-label" style={{ color: "#64748b" }}>{placeholder}</span>}
        </div>
        <span className="account-setting-select-chevron" aria-hidden="true">▾</span>
      </button>

      {isOpen && menuStyle ? createPortal(
        <div
          ref={menuRef}
          className="account-setting-select-menu"
          role="listbox"
          aria-label={title}
          style={menuStyle}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <input
            ref={inputRef}
            className="account-auth-input"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={placeholder}
            style={{ marginBottom: "4px" }}
          />
          {String(query || "").trim().length < minQueryLength ? (
            <div style={{ padding: "10px 12px", color: "#64748b", fontSize: "13px" }}>
              Type at least {minQueryLength} letters.
            </div>
          ) : filteredOptions.length === 0 ? (
            <div style={{ padding: "10px 12px", color: "#64748b", fontSize: "13px" }}>{emptyMessage}</div>
          ) : filteredOptions.map((option) => {
            const isSelected = option.value === value;

            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`account-setting-select-option${isSelected ? " account-setting-select-option--selected" : ""}`}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
              >
                <div className="account-setting-select-option-content">
                  {renderOption
                    ? renderOption(option.value, option)
                    : <span className="account-setting-select-preview-label">{option.label}</span>}
                </div>
                {isSelected ? <span className="account-setting-select-option-check" aria-hidden="true">✓</span> : null}
              </button>
            );
          })}
        </div>,
        document.body
      ) : null}
    </div>
  );
}